/* eslint-disable react/no-unused-state */
import { debounce, defer, isString } from 'lodash';
import React, { KeyboardEventHandler, MouseEventHandler } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { BBCommonProps, BBUiConfig, EditorMode, StoryModel } from 'types/story';
import { StoryFacade } from 'common/utils';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { triggerFocus } from 'common/utils/helpers';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import { ClientReducerState } from 'client/reducers';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { CHILDREN_KEY } from 'client/components/common/BuildingBlocks/utils/common';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { FIELD_EDITABLE_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import css from './Text.scss';
import ContentEditable from './ContentEditable';

type State = {
	html: string;
	editing: boolean;
};

type Props = BBCommonProps & { children?: string };

class Text extends React.Component<Props, State> {
	editableRef = React.createRef<HTMLDivElement>();

	constructor(props, context) {
		super(props, context);

		this.state = {
			html: props.children,
			editing: false,
		};
	}

	static getDerivedStateFromProps({ children }: Props, state: State): Partial<State> | null {
		const isUpdatedChildrenFromProp = document.activeElement === document.body && children !== state.html;

		if (isUpdatedChildrenFromProp) {
			return { html: children, editing: false };
		}

		return null;
	}

	onChangeDebounced = debounce((getHtml: () => string) => {
		const html = getHtml();
		if (this.state.html !== html) this.save(html, true);
	}, 500);

	onEditComplete = () => {
		this.onChangeDebounced.flush();
		this.disableEditing();
	};

	save = (html: string, isContinueEditing: boolean = false) => {
		const path = this.props.editableModeProps?.nodeProps?.['data-path'];
		const { currentMediaQuery } = this.props;

		if (!path || !this.state.editing) {
			console.warn("path isn't defined...");
			return;
		}

		// NOTE: children is always writes to defaultState, but for certain media query
		const childrenPath = `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}`;

		const payload =
			this.props.editorMode === EditorMode.CONTENT
				? { value: { [this.props.mediaQuery.defaultPlatform]: html }, path: childrenPath }
				: { value: html, path: `${childrenPath}.${currentMediaQuery}` };

		transmitToAdminBbUpdate({
			id: Text.name,
			isStory: isLayerType(this.props).global,
			...payload,
		});

		this.setState({ editing: isContinueEditing, html });
	};

	updateSelectionRect = (p: { getHtml: () => string }) => {
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);

		this.onChangeDebounced(p.getHtml);
	};

	enableEditing = () => {
		this.setState({ editing: true }, () => {
			// update SelectionHint and place cursor into contenteditable block
			defer(() => triggerFocus(this.editableRef.current?.querySelector('[contenteditable="true"]')));
		});
	};

	disableEditing = () => this.setState({ editing: false });

	renderEditable() {
		const { editing } = this.state;
		const { editableModeProps, uiConfig } = this.props;
		const nodeProps: BBUiConfig['nodeProps'] = {
			...uiConfig.nodeProps,
			...editableModeProps?.nodeProps,
			style: {
				...uiConfig.nodeProps?.style,
				...editableModeProps?.nodeProps?.style,
			},
		};

		const props: Partial<BBCommonProps['eventListeners']> &
			BBUiConfig['nodeProps'] & {
				className: string;
				ref: React.RefObject<HTMLDivElement>;
				onKeyUp?: KeyboardEventHandler;
				onKeyDown?: KeyboardEventHandler;
				onDoubleClick?: MouseEventHandler;
				dangerouslySetInnerHTML?: React.DOMAttributes<Element>['dangerouslySetInnerHTML'];
			} = {
			...nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
			ref: this.editableRef,
			className: cn(css.text, css.editable, uiConfig.nodeProps.className),
		};

		if (editing) {
			props[FIELD_EDITABLE_ATTR] = ''; // prevent from dragging by <Points />
			props.onKeyUp = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
			props.onKeyDown = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
		} else {
			if (this.props.editorMode === EditorMode.CONTENT) props.onClick = this.enableEditing;
			props.onDoubleClick = this.enableEditing;
			props.dangerouslySetInnerHTML = { __html: this.state.html };
		}

		return (
			<div {...props}>
				{editing ? (
					<ContentEditable
						html={this.state.html}
						onChange={this.updateSelectionRect}
						onComplete={this.onEditComplete}
						editableRef={props.ref}
					/>
				) : null}
			</div>
		);
	}

	renderDefault() {
		const { nodeProps, componentProps } = this.props.uiConfig;
		const Component = this.state.html.match(/\$[^0-9][a-z]*/gi) ? DynamicText : DefaultText;
		const tag = componentProps[UNIFORM_PROPS.tag];

		return (
			<Component
				nodeProps={nodeProps}
				stateAttrs={this.props.stateAttrs}
				eventListeners={this.props.eventListeners}
				tag={isString(tag) ? tag : 'p'}
				html={this.state.html}
				containerRef={this.props.containerRef}
				className={cn(css.text, nodeProps.className)}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

type TextProps = {
	nodeProps: Props['uiConfig']['nodeProps'];
	stateAttrs: Props['stateAttrs'];
	eventListeners: Props['eventListeners'];
	containerRef: Props['containerRef'];
	className: string;
	html: string;
	tag: React.ElementType | string;
};

const DefaultText = ({ tag: Tag, ...props }: TextProps) => (
	<Tag
		{...props.nodeProps}
		{...props.stateAttrs}
		{...props.eventListeners}
		ref={props.containerRef}
		className={cn(css.text, props.nodeProps.className)}
		dangerouslySetInnerHTML={{ __html: props.html }}
	/>
);

const DynamicText = (props: TextProps) => {
	const story = useSelector((state: ClientReducerState) => state.story as StoryModel);
	return <DefaultText {...props} html={new StoryFacade(story).getTransformedDynamicText(props.html)} />;
};

export default withCardTransitionContext(Text);
